/**
 * MACRO #32 — shared Genesis read hooks.
 *
 * ONE overview request is shared across every badge (react-query dedupes by
 * key), so a grid of cards costs a single network call. Only DESIGNATED
 * parcels appear in the overview, so a non-Genesis parcel resolves to `null`
 * and renders no badge.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchGenesisOverview, type GenesisOverview, type GenesisTerritory } from "../../lib/genesis-api";

export function useGenesisOverview() {
  return useQuery<GenesisOverview>({
    queryKey: ["genesis", "overview"],
    queryFn: ({ signal }) => fetchGenesisOverview(signal),
    staleTime: 60_000,
    retry: 1,
  });
}

export function useGenesisParcel(parcelId: number | string | null | undefined): {
  member: GenesisTerritory | null;
  isLoading: boolean;
} {
  const q = useGenesisOverview();
  const pid = parcelId == null ? null : Number(parcelId);
  const member = useMemo(() => {
    if (pid == null || Number.isNaN(pid) || !q.data) return null;
    return q.data.territories.find((t) => t.parcel_id === pid) ?? null;
  }, [pid, q.data]);
  return { member, isLoading: q.isLoading };
}
